import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { makeTestId } from './Declarative';
import { PortalEventTrap, PortalEventTrapProps } from './PortalEventTrap';
import { convertToStatefulComponentClass, getComponentName } from './ReactHelpers';

export interface StackingComponentProps {
  /**
   * The test id that will be added to the very root component that will wrap the children components. The Root suffix will be added
   */
  wrapperTestId: string;

  /**
   * The stacking root is the element container into which the mount point for Portals will be appended.
   * @deprecated no longer necessary for the majority of cases; try just deleting it. Otherwise, instead use `StackingRootContext.Provider`
   */
  stackingRootId?: string;

  /**
   * At the moment vizclient uses z-indexing in several places to order stacking.  Until this is resolved we need to pass in
   * a z-index prop to allow vizclient to properly stack things.
   * TODO: Feature 579115:Web: Unify Stacking strategy in viz client
   */
  zIndex?: number;

  /**
   * The StackingComponentWrapper blocks events from bubbling out of its Portal (i.e. negating the default behavior
   * of React Portals). This prop can be used to override this behavior on a per-event basis. For example,
   * `onClick: () => { return; }` will restore the default bubbling-out-of-portal behavior for onClick events.
   */
  portalEventTrapOverrides?: PortalEventTrapProps;
}

/**
 * The stacking root is the element container into which the mount point for Portals will be appended.
 */
export const StackingRootContext = React.createContext<HTMLElement | null>(null);
StackingRootContext.displayName = 'StackingRootContext';
function getStackingRootElement(stackingRootId: string | undefined, context: StackingComponentWrapper['context']): HTMLElement {
  const stackingRoot: HTMLElement | null = document.getElementById(stackingRootId!) || context || document.body;
  if (!stackingRoot) {
    throw new Error('Could not find stacking root.');
  }
  return stackingRoot;
}

export class StackingComponentWrapper extends React.Component<StackingComponentProps & PortalEventTrapProps> {
  public context!: React.ContextType<typeof StackingRootContext>;

  private stackingRoot: HTMLElement;
  private mountPoint: HTMLElement;

  public constructor(props: StackingComponentProps, context: StackingComponentWrapper['context']) {
    super(props);

    // tslint:disable-next-line:deprecation
    const { stackingRootId } = props;
    if (stackingRootId) {
      console.warn('`stackingRootId` is deprecated and no longer necessary for the vast majority of cases.');
    }

    this.stackingRoot = getStackingRootElement(stackingRootId, context);

    /*
     * Create the mount point where the stacked element will be mounted and append it to the stacking root.  It should align
     * with the top/left of the stacking root as to align the positioning of stacked elements with the stacking root.  This prevents
     * cases where there are relatively positioned elements above the mount point, thus making the top of the mount point below the top
     * of the stacking root.
     */

    this.mountPoint = document.createElement('div');
    this.mountPoint.style.position = 'absolute';
    this.mountPoint.style.top = '0';
    this.mountPoint.style.left = '0';
    this.updateMountPoint();
  }

  public componentDidMount(): void {
    this.stackingRoot.appendChild(this.mountPoint);
  }

  public componentDidUpdate(prevProps: StackingComponentProps): void {
    // tslint:disable-next-line:deprecation
    const { stackingRootId, wrapperTestId, zIndex } = this.props;

    const stackingRoot = getStackingRootElement(stackingRootId, this.context);
    if (stackingRoot !== this.stackingRoot) {
      this.stackingRoot = stackingRoot;
      this.stackingRoot.appendChild(this.mountPoint);
    }

    if (wrapperTestId !== prevProps.wrapperTestId || zIndex !== prevProps.zIndex) {
      this.updateMountPoint();
    }
  }

  public componentWillUnmount(): void {
    this.stackingRoot.removeChild(this.mountPoint);
  }

  public getMountPoint(): HTMLElement {
    return this.mountPoint;
  }

  public getStackingRoot(): HTMLElement {
    return this.stackingRoot;
  }

  private updateMountPoint(): void {
    this.mountPoint.style.zIndex = `${this.props.zIndex}`;
    this.mountPoint.setAttribute('data-tb-test-id', makeTestId(this.props.wrapperTestId, 'Root'));
  }

  public render(): React.ReactPortal {
    return ReactDOM.createPortal(
      <StackingRootContext.Provider value={this.mountPoint}>
        <PortalEventTrap {...this.props.portalEventTrapOverrides} children={this.props.children} />
      </StackingRootContext.Provider>,
      this.mountPoint
    );
  }
}
StackingComponentWrapper.contextType = StackingRootContext;

export const StackingComponentHOC = <P extends StackingComponentProps>(componentType: React.ComponentType<P>): React.ComponentClass<P> => {
  const componentClass: React.ComponentClass<P> = convertToStatefulComponentClass(componentType);

  const newComponentClass = class extends componentClass {
    public render(): JSX.Element {
      return <StackingComponentWrapper {...this.props} children={super.render()} />;
    }
  };
  newComponentClass.displayName = `StackingComponentHOC(${getComponentName(componentType)})`;

  return newComponentClass;
};
